/**
 * Cliente de la API de cuentas, progreso y flota.
 *
 * Va en un fichero aparte de `premium.ts` a propósito: aquello es el cobro y
 * las licencias, que están en producción y funcionan. Esto es lo nuevo.
 *
 * La sesión viaja en una cookie httpOnly que el navegador manda sola; aquí no
 * hay ningún token que guardar, y por eso todas las llamadas llevan
 * `credentials: 'same-origin'`. Nada de meter la sesión en localStorage: ahí la
 * lee cualquier script que se cuele en la página.
 */
import type { Answer, ExamResult, ModuleId, Question } from './types';
import { addOficiales } from './data';

export interface Usuario {
  email: string;
  verificado: boolean;
  creadaEl: string;
}

export interface Racha {
  actual: number;
  mejor: number;
  hoyCuenta: boolean;
  congelada: boolean;
}

export interface ModuloProgreso {
  vistas: number;
  dominadas: number;
  respuestas: number;
  porcentaje: number | null;
}

export interface Yo {
  usuario: Usuario | null;
  licencia: { key: string; kind: string } | null;
  racha: Racha | null;
  porModulo: Record<string, ModuloProgreso>;
  pendientes: number;
  flotas: { id: string; nombre: string | null }[];
}

export interface RespuestaRegistrada {
  id: string;
  chosen: number;
}

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function pedir<T>(url: string, opciones: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: opciones.body ? { 'Content-Type': 'application/json' } : undefined,
    ...opciones,
  });

  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}) as { error?: string });
    throw new ApiError(cuerpo.error ?? 'No se ha podido completar la operación', res.status);
  }
  return res.json() as Promise<T>;
}

const enviar = <T>(url: string, datos: unknown, method = 'POST') =>
  pedir<T>(url, { method, body: JSON.stringify(datos) });

// ── cuenta ────────────────────────────────────────────────────

export const registrar = (email: string, password: string, next = '/panel') =>
  enviar<{ usuario?: Usuario; pendiente?: true }>('/api/auth/register', { email, password, next });

export const entrar = (email: string, password: string, recordar = true) =>
  enviar<{ usuario: Usuario }>('/api/auth/login', { email, password, recordar });

export const salir = () => enviar<{ ok: true }>('/api/auth/logout', {});

export const reenviarVerificacion = (next = '/panel') => enviar<{ ok: true }>('/api/auth/verify/resend', { next });

export const olvidada = (email: string) => enviar<{ ok: true }>('/api/auth/forgot', { email });

export const restablecer = (token: string, password: string) =>
  enviar<{ ok: true }>('/api/auth/reset', { token, password });

export const cambiarPassword = (actual: string, nueva: string) =>
  enviar<{ ok: true }>('/api/auth/password', { actual, nueva });

export const borrarCuenta = (password: string) =>
  enviar<{ ok: true }>('/api/me', { password }, 'DELETE');

export const urlExportarDatos = '/api/me/export';

/** Estado de la sesión. Devuelve `usuario: null` si no hay ninguna abierta. */
export const yo = () => pedir<Yo>('/api/me');

// ── preguntas con cuenta ──────────────────────────────────────

/**
 * Descarga las 107 oficiales que exigen cuenta y las mete en el banco.
 * Devuelve cuántas añadió; 0 si no hay sesión o falta verificar el correo.
 */
export async function cargarOficiales(): Promise<number> {
  try {
    const { questions } = await pedir<{ questions: Question[] }>('/api/questions/oficiales');
    return addOficiales(questions);
  } catch {
    return 0;
  }
}

// ── progreso ──────────────────────────────────────────────────

/**
 * Registra respuestas. `sesion` en true marca que se ha completado una tanda o
 * un simulacro: eso hace que el día cuente para la racha aunque sean menos de
 * diez preguntas.
 */
export const registrarRespuestas = (respuestas: RespuestaRegistrada[]) =>
  enviar<{ registradas: number; racha: Racha }>('/api/progress', { respuestas });

export const guardarIntento = (
  questions: Question[],
  answers: Answer[],
  result: ExamResult,
  mode: 'exam' | 'practice' = 'exam',
) =>
  enviar<{ ok: true; racha: Racha; result: ExamResult }>('/api/attempts', {
    mode,
    elapsedMs: result.elapsedMs,
    respuestas: questions.flatMap((q) => {
      const chosen = answers.find((a) => a.questionId === q.id)?.chosen;
      return chosen === null || chosen === undefined ? [] : [{ id: q.id, chosen }];
    }),
  });

export interface Intento {
  at: string;
  mode: 'exam' | 'practice';
  correct: number;
  total: number;
  passed: boolean;
  elapsedMs: number;
  modules: ExamResult['modules'];
}

export const historial = () => pedir<{ intentos: Intento[] }>('/api/attempts');

/** Ids que tocan hoy en el modo estudio. */
export const paraEstudiar = (module?: ModuleId, limite = 10) =>
  pedir<{ ids: string[] }>(
    `/api/study?limite=${limite}${module ? `&module=${module}` : ''}`,
  );

/**
 * Sube al servidor lo practicado antes de crear la cuenta. Se llama una sola
 * vez, justo después del alta: la racha que traía el usuario es lo que le
 * convenció de registrarse, así que perderla ahí sería el peor momento.
 */
export const importarProgreso = (datos: {
  respuestas?: RespuestaRegistrada[];
  intentos?: unknown[];
  dias?: { day: string; answered: number; correct: number }[];
}) => enviar<{ dias: number; intentos: number; racha: Racha }>('/api/progress/import', datos);

// ── flota ─────────────────────────────────────────────────────

export interface Conductor {
  key: string;
  etiqueta: string | null;
  email: string | null;
  estado: 'libre' | 'invitado' | 'activo' | 'sin consentimiento';
  racha?: number;
  simulacros?: number;
  porModulo?: Record<string, ModuloProgreso>;
  listo?: boolean;
  ultimaActividad?: string | null;
}

export interface PanelFlota {
  id: string;
  nombre: string | null;
  total: number;
  activos: number;
  sinEmpezar: number;
  conductores: Conductor[];
}

export const panelFlota = (fleetId: string) =>
  pedir<PanelFlota>(`/api/fleets/${encodeURIComponent(fleetId)}`);

export const invitarConductores = (fleetId: string, emails: string[]) =>
  enviar<{ creadas: number; sinAsiento: number }>(`/api/fleets/${encodeURIComponent(fleetId)}/invite`, {
    emails,
  });

export const renombrarFlota = (fleetId: string, nombre: string) =>
  enviar<{ ok: true }>(`/api/fleets/${encodeURIComponent(fleetId)}/name`, { nombre });

export const etiquetarConductor = (fleetId: string, key: string, label: string) =>
  enviar<{ ok: true }>(`/api/fleets/${encodeURIComponent(fleetId)}/label`, { key, label });

export const invitacion = (code: string) =>
  pedir<{ email: string; flota: string; fleetId: string | null }>(`/api/fleet/invite/${encodeURIComponent(code)}`);

/** Canje de la invitación. El consentimiento es obligatorio y explícito. */
export const canjearInvitacion = (code: string, consentimiento: boolean) =>
  enviar<{ licencia: { key: string } }>('/api/fleet/redeem', {
    code,
    consentimiento,
    version: 'flota-2026-07-27',
  });

export const retirarConsentimientoFlota = (licenseKey: string) =>
  enviar<{ ok: true }>('/api/fleet/consent', { licenseKey }, 'DELETE');

export const urlCsvFlota = (fleetId: string) =>
  `/api/fleets/${encodeURIComponent(fleetId)}/export.csv`;

export { ApiError };
